<template>
  <div class="supersession-manager">
    <!-- Header -->
    <div class="header-section">
      <h3 class="title">Supersessions for: {{ unificado }}</h3>
    </div>

    <!-- Filter Input -->
    <div class="filter-section">
      <div class="input-wrapper">
        <i class="mdi mdi-magnify search-icon"></i>
        <input
          id="filter-input"
          type="text"
          class="filter-input"
          placeholder="Filter supersessions..."
          v-model="filter"
        />
        <button v-if="filter" @click="filter = ''" class="clear-button">
          <i class="mdi mdi-close"></i>
        </button>
      </div>
    </div>

    <!-- Add New Supersession -->
    <div v-if="userCanEditProducts" class="add-section">
      <div class="input-group">
        <div class="inputs-container">
          <input
            id="supersession-input"
            type="text"
            class="form-input"
            placeholder="New supersession"
            v-model="newSS"
            :disabled="loading"
          />
          <input
            type="text"
            class="form-input"
            placeholder="Extra info"
            v-model="newSSInfo"
            :disabled="loading"
          />
        </div>
        <button
          @click="createSupersession()"
          class="add-button"
          :disabled="loading || !newSS"
          title="Add supersession"
        >
          <i class="mdi mdi-plus-circle"></i>
          <span class="hidden md:inline ml-2">Add</span>
        </button>
      </div>
    </div>

    <!-- Supersessions List -->
    <div class="supersessions-list">
      <SupersessionLine
        v-for="supersession in supersessionsFiltered"
        :supersession="supersession"
        :key="supersession.ID"
        @deletedSupersession="
          id => {
            supersessions = supersessions.filter(supersession => {
              return supersession.ID != id;
            });
          }
        "
      />
      <div v-if="supersessionsFiltered.length === 0" class="empty-state">
        <i class="mdi mdi-package-variant-closed"></i>
        <p>
          {{
            filter
              ? "No supersessions match your filter"
              : "No supersessions found"
          }}
        </p>
      </div>
    </div>
  </div>
</template>
<script>
import {
  getSupersessionsForPart,
  createSupersession
} from "@/api/store/SupersessionsService.js";
import SupersessionLine from "@/components/product/SupersessionLine.vue";
export default {
  components: {
    SupersessionLine
  },
  props: {
    unificado: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      supersessions: [],
      filter: "",
      newSS: "",
      newSSInfo: "",
      loading: false
    };
  },
  computed: {
    userCanEditProducts() {
      return this.$store.getters["auth/userHasPermission"]("can_edit_products");
    },
    supersessionsFiltered() {
      let supersessionsFiltered = [...this.supersessions];
      if (this.filter != "" && supersessionsFiltered) {
        supersessionsFiltered = supersessionsFiltered.filter(ss => {
          return ss.ALTERNATIVE.toLowerCase().includes(
            this.filter.toLowerCase()
          );
        });
      }
      return supersessionsFiltered;
    }
  },
  mounted() {
    this.loadSupersessions();
    document.getElementById("supersession-input").focus();
  },
  methods: {
    createSupersession() {
      this.loading = true;
      createSupersession(this.newSS, this.newSSInfo, this.unificado)
        .then(res => {
          this.supersessions.unshift(res.supersession);
          this.$toast.open({
            message: "Supersession added",
            type: "success",
            position: "top-right"
          });
          this.newSS = "";
          this.newSSInfo = "";
          this.loading = false;
          this.$nextTick(() => {
            document.getElementById("supersession-input").focus();
          });
        })
        .catch(e => {
          this.$toast.open({
            message: e?.response?.data?.message || "Error on request!",
            type: "error",
            position: "top-right"
          });
          this.loading = false;
        });
    },
    async loadSupersessions() {
      this.supersessions = await getSupersessionsForPart(this.unificado);
    }
  }
};
</script>
<style lang="scss" scoped>
@import "@/assets/style/css/generic-variables.scss";

.supersession-manager {
  padding: 20px;
  background: white;
  border-radius: 8px;

  @media (max-width: 768px) {
    padding: 15px;
  }

  .header-section {
    margin-bottom: 20px;
    padding-bottom: 15px;
    border-bottom: 2px solid #e5e7eb;

    @media (max-width: 768px) {
      margin-bottom: 15px;
      padding-bottom: 12px;
    }

    .title {
      font-size: 20px;
      font-weight: 600;
      color: #1f2937;
      margin: 0;

      @media (max-width: 768px) {
        font-size: 18px;
      }
    }
  }

  .filter-section {
    margin-bottom: 20px;

    @media (max-width: 768px) {
      margin-bottom: 15px;
    }

    .input-wrapper {
      position: relative;
      max-width: 600px;

      @media (max-width: 768px) {
        max-width: 100%;
      }

      .search-icon {
        position: absolute;
        left: 12px;
        top: 50%;
        transform: translateY(-50%);
        color: #6b7280;
        font-size: 20px;
        pointer-events: none;
      }

      .filter-input {
        width: 100%;
        padding: 10px 40px 10px 42px;
        border: 1px solid #d1d5db;
        border-radius: 8px;
        font-size: 15px;
        transition: border-color 0.2s, box-shadow 0.2s;

        @media (max-width: 768px) {
          font-size: 16px;
          padding: 12px 40px 12px 42px;
        }

        &:focus {
          outline: none;
          border-color: $link-color-1;
          box-shadow: 0 0 0 3px rgba(18, 110, 106, 0.1);
        }

        &::placeholder {
          color: #9ca3af;
        }
      }

      .clear-button {
        position: absolute;
        right: 8px;
        top: 50%;
        transform: translateY(-50%);
        background: transparent;
        border: none;
        padding: 4px;
        cursor: pointer;
        color: #6b7280;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 4px;
        transition: background-color 0.2s;

        &:hover {
          background-color: #f3f4f6;
        }

        i {
          font-size: 18px;
        }
      }
    }
  }

  .add-section {
    margin-bottom: 25px;
    padding: 15px;
    background: #f9fafb;
    border-radius: 8px;
    border: 1px solid #e5e7eb;

    @media (max-width: 768px) {
      margin-bottom: 20px;
      padding: 12px;
    }

    .input-group {
      display: flex;
      gap: 12px;
      align-items: stretch;

      @media (max-width: 768px) {
        flex-direction: column;
        gap: 10px;
      }

      .inputs-container {
        flex: 1;
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 12px;

        @media (max-width: 768px) {
          grid-template-columns: 1fr;
          gap: 10px;
        }

        .form-input {
          width: 100%;
          padding: 10px 12px;
          border: 1px solid #d1d5db;
          border-radius: 6px;
          font-size: 15px;
          transition: border-color 0.2s, box-shadow 0.2s;

          @media (max-width: 768px) {
            font-size: 16px;
            padding: 12px;
          }

          &:focus {
            outline: none;
            border-color: $link-color-1;
            box-shadow: 0 0 0 3px rgba(18, 110, 106, 0.1);
          }

          &:disabled {
            background-color: #f3f4f6;
            cursor: not-allowed;
            opacity: 0.6;
          }

          &::placeholder {
            color: #9ca3af;
          }
        }
      }

      .add-button {
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 10px 20px;
        background-color: $link-color-1;
        color: white;
        border: none;
        border-radius: 6px;
        font-size: 15px;
        font-weight: 500;
        cursor: pointer;
        transition: background-color 0.2s, transform 0.1s;
        white-space: nowrap;
        min-height: 44px;

        @media (max-width: 768px) {
          width: 100%;
          padding: 12px 20px;
          font-size: 16px;
        }

        i {
          font-size: 20px;
        }

        &:hover:not(:disabled) {
          background-color: darken($link-color-1, 10%);
        }

        &:active:not(:disabled) {
          transform: scale(0.98);
        }

        &:disabled {
          background-color: #d1d5db;
          cursor: not-allowed;
          opacity: 0.6;
        }
      }
    }
  }

  .supersessions-list {
    .empty-state {
      text-align: center;
      padding: 40px 20px;
      color: #6b7280;

      @media (max-width: 768px) {
        padding: 30px 15px;
      }

      i {
        font-size: 48px;
        margin-bottom: 12px;
        opacity: 0.5;

        @media (max-width: 768px) {
          font-size: 40px;
        }
      }

      p {
        margin: 0;
        font-size: 15px;

        @media (max-width: 768px) {
          font-size: 14px;
        }
      }
    }
  }
}
</style>
